/**
 * RdvSection — bloc pliable « Prendre rendez-vous en mairie » du pôle info.
 *
 * Affiché seulement si la mairie a au moins un motif actif (backend renvoie
 * `rdv: null` sinon → masquage auto). L'administré :
 *   · voit ses RDV à venir (avec annulation) ;
 *   · choisit un motif → voit les créneaux libres → réserve.
 *
 * Réservation confirmée direct (pas de validation mairie). Aucune modale
 * navigateur (Règle projet) — retours via message inline.
 */

import { useState } from 'react';
import { CalendarClock, MapPin, X, ExternalLink } from 'lucide-react';
import { SectionRepliable } from '@/components/ui/SectionRepliable';
import { Badge, Button, Input } from '@/components/ui';
import { api, ApiException, type RdvCitoyenPayload, type RdvCreneau } from '@/lib/api';
import { lienOfficielDemarche } from '@/lib/demarche-liens';

/** Date pure yyyy-mm-dd → « jeudi 3 juillet » (UTC, sans décalage). */
function formatDatePure(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });
}

export function RdvSection({
  data,
  onUpdated,
}: {
  data: RdvCitoyenPayload;
  onUpdated: (next: RdvCitoyenPayload | null) => void;
}) {
  const [motifSel, setMotifSel] = useState<string | null>(null);
  const [creneaux, setCreneaux] = useState<RdvCreneau[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Item 13.2 — n° de pré-demande ANTS. Demandé uniquement quand le motif porte
  // sur un titre d'identité : l'imposer sur « rencontre avec le maire » n'aurait
  // aucun sens.
  const [numeroPredemande, setNumeroPredemande] = useState('');

  const motifCourant = data.motifs.find((m) => m.id === motifSel) ?? null;

  async function refresh() {
    try {
      const next = await api.rdv.get();
      onUpdated(next);
    } catch { /* ignore */ }
  }

  async function ouvrirMotif(motifId: string) {
    setMotifSel(motifId);
    setCreneaux(null);
    setMsg(null);
    setBusy(true);
    try {
      const { creneaux } = await api.rdv.creneaux(motifId);
      setCreneaux(creneaux);
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Chargement des créneaux impossible');
    } finally {
      setBusy(false);
    }
  }

  // Item 13.2 (recette 02/08) — même règle que le serveur : 10 lettres/chiffres,
  // espaces et tirets tolérés à la saisie puis retirés.
  const predemandeNorm = numeroPredemande.replace(/[\s-]/g, '').toUpperCase();
  const motifExigeAnts = Boolean(
    motifCourant && lienOfficielDemarche(motifCourant.nom)?.url.includes('ants.gouv.fr'));
  const predemandeValide = /^[A-Z0-9]{10}$/.test(predemandeNorm);

  async function reserver(c: RdvCreneau) {
    if (!motifSel) return;
    if (motifExigeAnts && !predemandeValide) {
      setMsg(predemandeNorm
        ? 'Le numéro de pré-demande ANTS comporte 10 lettres ou chiffres (il figure sur votre récapitulatif).'
        : 'Le numéro de pré-demande ANTS est obligatoire : sans lui, le rendez-vous ne pourra pas être honoré.');
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.rdv.reserver({
        motifId: motifSel, date: c.date, heureDebut: c.heureDebut,
        // Le champ n'est proposé que pour un motif « titre d'identité » ; ailleurs
        // il reste vide et on n'envoie rien.
        numeroPredemande: motifExigeAnts ? predemandeNorm : null,
      });
      setMotifSel(null);
      setCreneaux(null);
      setNumeroPredemande('');
      setMsg('Rendez-vous confirmé. Vous le retrouvez dans « Mes rendez-vous ».');
      await refresh();
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Réservation impossible');
      // Recharger les créneaux (celui-ci vient peut-être d'être pris).
      if (motifSel) { try { const { creneaux } = await api.rdv.creneaux(motifSel); setCreneaux(creneaux); } catch { /* */ } }
    } finally {
      setBusy(false);
    }
  }

  async function annuler(id: string) {
    setBusy(true);
    setMsg(null);
    try {
      await api.rdv.annuler(id);
      setMsg('Rendez-vous annulé.');
      await refresh();
    } catch (e) {
      setMsg(e instanceof ApiException ? e.message : 'Annulation impossible');
    } finally {
      setBusy(false);
    }
  }

  // Regrouper les créneaux par date pour un affichage lisible.
  const creneauxParDate = new Map<string, RdvCreneau[]>();
  for (const c of creneaux ?? []) {
    const arr = creneauxParDate.get(c.date) ?? [];
    arr.push(c);
    creneauxParDate.set(c.date, arr);
  }

  return (
    <SectionRepliable
      title="Prendre rendez-vous en mairie"
      icon={<CalendarClock className="h-5 w-5 text-grc" aria-hidden="true" />}
      defaultOpen={false}
      right={data.mesRdv.length > 0
        ? <Badge variant="success" size="sm">{data.mesRdv.length} à venir</Badge>
        : <Badge variant="neutral" size="sm">{data.motifs.length} motif{data.motifs.length > 1 ? 's' : ''}</Badge>}
    >
      {/* Mes RDV à venir */}
      {data.mesRdv.length > 0 && (
        <div className="mb-3">
          <div className="mb-1 text-xs font-medium text-ink-muted">Mes rendez-vous</div>
          <ul className="space-y-2">
            {data.mesRdv.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3 rounded-r-lg border border-border bg-surface/50 p-3">
                <div className="min-w-0">
                  <div className="font-medium text-ink">{r.motifNom}</div>
                  <div className="mt-0.5 text-sm text-ink-muted">
                    {formatDatePure(r.dateRdv)} à {r.heureDebut}
                  </div>
                  {r.lieu && <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-ink-dim"><MapPin className="h-3 w-3" />{r.lieu}</div>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => annuler(r.id)} disabled={busy} title="Annuler">
                  <X className="h-4 w-4" /> Annuler
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Prise de RDV */}
      {!motifSel ? (
        <div>
          <div className="mb-1 text-xs font-medium text-ink-muted">Prendre un nouveau rendez-vous</div>
          <ul className="space-y-2">
            {data.motifs.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => ouvrirMotif(m.id)}
                  disabled={busy}
                  className="flex w-full items-center justify-between gap-3 rounded-r-lg border border-border bg-bg-2 p-3 text-left transition-colors hover:border-grc/40 hover:bg-grc-tint/20"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-ink">{m.nom}</div>
                    {m.description && <div className="mt-0.5 text-sm text-ink-muted">{m.description}</div>}
                    <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-dim">
                      <span>{m.dureeMin} min</span>
                      {m.lieu && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{m.lieu}</span>}
                    </div>
                  </div>
                  <CalendarClock className="h-4 w-4 flex-shrink-0 text-grc" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium text-ink">Créneaux — {motifCourant?.nom}</div>
            <Button variant="ghost" size="sm" onClick={() => { setMotifSel(null); setCreneaux(null); }}>
              Retour
            </Button>
          </div>
          {/* Item 13.2 — le champ n'apparaît que pour un motif « titre d'identité ».
              La détection réutilise `lienOfficielDemarche`, qui sait déjà
              reconnaître CNI, passeport, permis et carte grise : écrire une
              seconde liste de mots-clés ici, c'était garantir qu'un jour les deux
              divergent. */}
          {motifCourant && lienOfficielDemarche(motifCourant.nom)?.url.includes('ants.gouv.fr') && (
            <div className="mb-3 rounded-r-lg border border-border bg-surface/50 p-3">
              <label htmlFor="rdv-predemande" className="text-sm font-medium text-ink">
                Numéro de pré-demande ANTS <span className="ml-0.5 text-urgent" aria-label="requis">*</span>
              </label>
              <p className="mb-2 mt-0.5 text-xs text-ink-muted">
                10 lettres ou chiffres, sur le récapitulatif de votre pré-demande.{' '}
                <strong>Sans numéro de pré-demande valide, le rendez-vous ne
                pourra pas être honoré</strong> — l'agent en a besoin pour
                rappeler votre dossier au guichet.
              </p>
              <Input
                id="rdv-predemande"
                value={numeroPredemande}
                onChange={(e) => setNumeroPredemande(e.target.value)}
                placeholder="Par exemple 1234567890"
                maxLength={60}
              />
              <a
                href={lienOfficielDemarche(motifCourant.nom)!.url}
                target="_blank" rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-grc hover:underline"
              >
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
                {lienOfficielDemarche(motifCourant.nom)!.label}
              </a>
            </div>
          )}

          {busy && !creneaux ? (
            <p className="text-sm text-ink-muted">Chargement…</p>
          ) : creneauxParDate.size === 0 ? (
            <p className="text-sm text-ink-muted">
              Aucun créneau disponible pour le moment. Réessayez plus tard ou contactez la mairie.
            </p>
          ) : (
            <div className="space-y-3">
              {[...creneauxParDate.entries()].map(([date, cs]) => (
                <div key={date}>
                  <div className="mb-1 text-xs font-medium capitalize text-ink-dim">{formatDatePure(date)}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {cs.map((c) => (
                      <button
                        key={`${c.date}-${c.heureDebut}`}
                        type="button"
                        onClick={() => reserver(c)}
                        disabled={busy}
                        className="rounded-r-pill border border-border bg-surface px-3 py-1 text-sm text-ink transition-colors hover:border-grc hover:bg-grc-tint/30 disabled:opacity-50"
                      >
                        {c.heureDebut}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {msg && <p className="mt-2 text-xs text-ink-muted" role="status">{msg}</p>}
    </SectionRepliable>
  );
}
